/*
  The state marks of an element or a line, in words. Colour is for state only (the visual language), and a state is
  always told in words as well, so the meaning never rests on colour alone.
*/

import type { ElementRow, LineRow } from "../../../api/types";
import { displayState, STATE_WORDS, type DisplayState } from "../../../ui";

export interface Badge {
  key: string;
  text: string;
  tone: DisplayState | "neutral";
}

type ElementLike = Pick<ElementRow, "state" | "origin" | "pending" | "removal" | "not_used_by_us">;
type LineLike = Pick<LineRow, "state" | "origin" | "pending" | "removal" | "sme_comment">;

function baseBadge(state: ElementRow["state"], origin: ElementRow["origin"]): Badge | null {
  if (state === "accepted") return { key: "state", text: "Accepted, waiting for the SME", tone: "waiting" };
  if (state === "added_by_hand") return { key: "state", text: "Added by hand, waiting for the SME", tone: "waiting" };
  if (state === "withdrawn") return { key: "state", text: "Withdrawn", tone: "neutral" };
  if (state === "removed") return { key: "state", text: "Removed", tone: "neutral" };
  const shown = displayState(state, { origin });
  return shown ? { key: "state", text: STATE_WORDS[shown], tone: shown } : null;
}

function pendingBadge(pending: ElementLike["pending"] | LineLike["pending"]): Badge | null {
  if (!pending) return null;
  if (pending.status === "sent_back") return { key: "pending", text: "Next version sent back", tone: "sent_back" };
  return {
    key: "pending",
    text: pending.light ? "Edited since you validated, waiting for the SME" : "Next version waiting for the SME",
    tone: "waiting",
  };
}

function removalBadge(removal: ElementLike["removal"] | LineLike["removal"]): Badge | null {
  if (!removal) return null;
  if (removal.status === "sent_back") return { key: "removal", text: "Removal sent back", tone: "sent_back" };
  return { key: "removal", text: "Marked for removal, waiting for the SME", tone: "waiting" };
}

/** The marks to draw beside an element's name: its state, then a Next version, a removal mark, "not used by us". */
export function elementBadges(row: ElementLike): Badge[] {
  const out: Badge[] = [];
  const base = baseBadge(row.state, row.origin);
  if (base) out.push(base);
  const pending = pendingBadge(row.pending);
  if (pending) out.push(pending);
  const removal = removalBadge(row.removal);
  if (removal) out.push(removal);
  if (row.not_used_by_us) out.push({ key: "not_used", text: "Not used by us", tone: "neutral" });
  return out;
}

/** The marks of a line. A line with no state of its own follows its element, so it shows only what is its own. */
export function lineBadges(line: LineLike): Badge[] {
  const out: Badge[] = [];
  if (line.state === "sent_back") out.push({ key: "state", text: "Line returned", tone: "sent_back" });
  else if (line.state) {
    const base = baseBadge(line.state, line.origin);
    if (base) out.push(base);
  } else if (line.origin === "loaded") out.push({ key: "state", text: "Loaded", tone: "loaded" });
  else if (line.origin === "app") out.push({ key: "state", text: "Made by the app", tone: "neutral" });
  const pending = pendingBadge(line.pending);
  if (pending) out.push(pending);
  const removal = removalBadge(line.removal);
  if (removal) out.push(removal);
  return out;
}

/**
 * A state in words, for places that only have the state's name (a document link, a chapter, a phase setting): never the raw
 * token such as "added_by_hand" (UI-16).
 */
export function stateWords(state: string | null | undefined): string {
  if (!state) return "";
  const known = baseBadge(state as ElementRow["state"], "work");
  if (known) return known.text;
  const spaced = state.replace(/_/g, " ").trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : spaced;
}

/** Whether the element has been validated (or is loaded), so a change to it becomes a Next version. */
export function isSettled(row: Pick<ElementRow, "state" | "origin">): boolean {
  return row.state === "validated" || (row.state == null && row.origin === "loaded");
}

/** Whether an element can be withdrawn: it was never validated and is not gone already. */
export function canWithdraw(row: Pick<ElementRow, "state" | "origin">): boolean {
  if (row.origin === "loaded") return false;
  return row.state === "accepted" || row.state === "added_by_hand" || row.state === "sent_back" || row.state === "rejected";
}

/** Whether a validated element the discipline added can be marked for removal. */
export function canMarkRemoval(row: Pick<ElementRow, "state" | "origin" | "removal">): boolean {
  return row.state === "validated" && row.origin !== "loaded" && !row.removal;
}

/** The element is gone from the screens that edit. */
export function isGone(row: Pick<ElementRow, "state">): boolean {
  return row.state === "withdrawn" || row.state === "removed";
}

/**
 * The name of the colleague whose Next version waits on the element, or null when none waits or it is one's own (UI-11,
 * DEC-1016-96 R2-07). A second engineer may add to it (`add_to_pending`) or wait; a Next version sent back is not added to.
 */
export function othersPending(row: Pick<ElementRow, "pending" | "pending_by">, membershipId: string | null | undefined): string | null {
  if (!row.pending || row.pending.status !== "waiting" || !membershipId || row.pending.made_by === membershipId) return null;
  return row.pending_by?.display || "a colleague";
}
